import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Utensils, Star, Award, HandHeart, Sparkles, Quote, Instagram } from "lucide-react";
import { useLang } from "@/i18n";
import api, { resolveMediaUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import ScrollReveal from "@/components/ScrollReveal";
import AnimatedCounter from "@/components/AnimatedCounter";
import InstagramFeed from "@/components/InstagramFeed";

const HERO_IMG = "https://images.unsplash.com/photo-1661994215679-cde7c2c5c060?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
  const { t, tt, isRTL } = useLang();
  const [media, setMedia] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [menu, setMenu] = useState(null);
  const [chefs, setChefs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [posts, setPosts] = useState([]);
  const [certs, setCerts] = useState([]);
  const [settings, setSettings] = useState({});
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/media").then((r) => {
      // Prefer videos first (autoplaying tiles are more eye-catching), then photos.
      const sorted = [...r.data].sort((a, b) => {
        if (a.type === b.type) return 0;
        return a.type === "video" ? -1 : 1;
      });
      setMedia(sorted.slice(0, 6));
    }).catch(() => {});
    api.get("/hotels").then((r) => setHotels(r.data.slice(0, 4))).catch(() => {});
    api.get("/menus/current").then((r) => setMenu(r.data)).catch(() => {});
    api.get("/chefs").then((r) => setChefs(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/posts", { params: { published: true } }).then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
    api.get("/certificates").then((r) => setCerts(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(Object.fromEntries(r.data.map((s) => [s.key, s.value])))).catch(() => {});
  }, []);

  const heroVideo = settings.hero_video_url;
  const instagramUrl = settings.instagram_url;

  return (
    <div>
      <SEO />
      {/* HERO */}
      <section className="relative isolate overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 -z-10"
          style={{ transform: `translate3d(0, ${scrollY * 0.25}px, 0)`, willChange: "transform" }}
        >
          {heroVideo ? (
            <video
              src={resolveMediaUrl(heroVideo)}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <img src={HERO_IMG} alt="" className="w-full h-full object-cover animate-kenburns" />
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-brand-navy/90 via-brand-navy/70 to-brand-navy/40" />
        </div>
        <div
          className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36 text-canvas-cream"
          style={{ transform: `translate3d(0, ${scrollY * -0.08}px, 0)`, opacity: Math.max(0, 1 - scrollY / 900) }}
        >
          <div className="text-xs md:text-sm uppercase tracking-[0.35em] text-brand-gold mb-6 animate-fade-up" data-testid="hero-kicker">
            {t("hero.kicker")}
          </div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl animate-fade-up" data-testid="hero-title">
            {t("hero.title")}
          </h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-canvas-cream/85 animate-fade-up" data-testid="hero-subtitle">
            {t("hero.subtitle")}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4 animate-fade-up">
            <Link
              to="/hotels"
              data-testid="hero-cta-hotels"
              className="shine-btn inline-flex items-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white px-7 py-3 font-medium transition shadow-lg shadow-brand-orange/30"
            >
              {t("hero.cta_hotels")}
              <ArrowRight size={18} className={isRTL ? "rtl:rotate-180" : ""} />
            </Link>
            <Link
              to="/menu"
              data-testid="hero-cta-menu"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 hover:bg-white/10 text-white px-7 py-3 font-medium transition backdrop-blur"
            >
              {t("hero.cta_menu")}
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-3 gap-4 md:gap-8 max-w-3xl">
            <Stat n="25000+" label={{ tr: "Günlük tabak", ar: "طبق يومي", en: "Daily plates", ru: "Блюд в день" }} />
            <Stat n="25" label={{ tr: "Partner otel", ar: "فندق شريك", en: "Partner hotels", ru: "Отелей-партнёров" }} />
            <Stat n="25+" label={{ tr: "Yıllık deneyim", ar: "سنة خبرة", en: "Years of craft", ru: "Лет мастерства" }} />
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-canvas-cream/70 animate-fade-up" aria-hidden="true">
          <span className="text-[10px] uppercase tracking-[0.4em]">Scroll</span>
          <div className="h-10 w-[2px] bg-canvas-cream/40 relative overflow-hidden rounded-full">
            <span className="absolute top-0 left-0 right-0 h-3 bg-brand-gold rounded-full animate-[scrolldown_1.8s_ease-in-out_infinite]" />
          </div>
        </div>
      </section>

      {/* Why Ariaf */}
      <section className="section-warm py-24" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <ScrollReveal>
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.35em] text-brand-orange font-semibold mb-3">
              <Sparkles size={12} className="inline mb-0.5"/> {t("sections.why_title")}
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-navy">{t("sections.why_desc")}</h2>
          </div>
          </ScrollReveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <ScrollReveal delay={0}>
            <WhyCard
              icon={<Award className="text-brand-orange" size={22} />}
              title={{ tr: "Yılların Ustalığı", ar: "خبرة السنين", en: "Years of Craft", ru: "Годы мастерства" }}
              desc={{
                tr: "25 yılı aşkın süredir Umre yolcularının sofrasında; her tarifin arkasında bir usta, her tabağın arkasında sabır var.",
                ar: "أكثر من 25 عاماً على مائدة ضيوف العمرة؛ وراء كل وصفة أستاذ، وخلف كل طبق صبر.",
                en: "Over 25 years at the table of Umrah pilgrims — every recipe carries a master, every plate carries patience.",
                ru: "Более 25 лет за столом гостей умры — за каждым рецептом мастер, за каждым блюдом терпение.",
              }}
            />
            </ScrollReveal>
            <ScrollReveal delay={120}>
            <WhyCard
              icon={<HandHeart className="text-brand-orange" size={22} />}
              title={{ tr: "Kutsal Yolculuğa Saygı", ar: "احترام الرحلة المقدّسة", en: "Respect for the Sacred", ru: "Уважение к священному пути" }}
              desc={{
                tr: "Yemek, yolculuğun ruhunu tamamlar. Ariaf'ta her tabak, sizi bekleyen ibadet günlerinize saygıyla hazırlanır.",
                ar: "الطعام يُتمّم روح الرحلة. في أرياف، يُعَدّ كل طبق باحترام لأيام العبادة التي تنتظرك.",
                en: "Food completes the spirit of the journey. Every plate at Ariaf is crafted with respect for the days of worship ahead.",
                ru: "Еда завершает дух путешествия. Каждое блюдо Ariaf готовится с уважением к предстоящим дням поклонения.",
              }}
            />
            </ScrollReveal>
            <ScrollReveal delay={240}>
            <WhyCard
              icon={<Utensils className="text-brand-orange" size={22} />}
              title={{ tr: "Türk, Arap & Özbek Mutfağı", ar: "المطبخ التركي والعربي والأوزبكي", en: "Turkish, Arabic & Uzbek Cuisine", ru: "Турецкая, арабская и узбекская кухня" }}
              desc={{
                tr: "Menemenden mansafa, Özbek plovundan baklavaya — üç mutfağın en sevilenleri, aynı sofrada bir araya gelir.",
                ar: "من الشكشوكة إلى المنسف، من البلاو الأوزبكي إلى البقلاوة — أشهى ما في المطابخ الثلاثة على مائدة واحدة.",
                en: "From menemen to mansaf, from Uzbek plov to baklava — the beloved dishes of three cuisines meet on one table.",
                ru: "От меменена до мансафа, от узбекского плова до пахлавы — любимые блюда трёх кухонь на одном столе.",
              }}
            />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Gallery preview */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <ScrollReveal>
        <SectionHeader kicker={t("nav.gallery")} title={t("sections.gallery_title")} desc={t("sections.gallery_desc")} link="/gallery" />
        </ScrollReveal>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="home-gallery-grid">
          {media.map((m, i) => (
            <ScrollReveal
              key={m.id}
              delay={i * 80}
              className={i === 0 ? "col-span-2 row-span-2" : ""}
            >
            <div className={`media-zoom premium-card relative rounded-2xl overflow-hidden group aspect-square w-full h-full`} data-testid={`gallery-item-${i}`}>
              {m.type === "video" ? (
                <>
                  <video src={resolveMediaUrl(m.url)} muted loop playsInline autoPlay className="w-full h-full object-cover" />
                  <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 rounded-full bg-brand-orange/95 text-white text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 backdrop-blur">
                    ● Video
                  </span>
                </>
              ) : (
                <img src={resolveMediaUrl(m.url)} alt={tt(m.caption)} className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
              <div className="absolute bottom-3 left-3 right-3 text-white text-sm opacity-0 group-hover:opacity-100 transition">{tt(m.caption)}</div>
            </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Chefs */}
      {chefs.length > 0 && (
        <section className="section-band py-24" data-testid="home-chefs">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <SectionHeader
              kicker={<><Utensils size={12} className="inline mb-0.5"/> {t("sections.chefs_title")}</>}
              title={t("sections.chefs_title")}
              desc={t("sections.chefs_desc")}
            />
            <div className="mt-12 grid gap-8 md:grid-cols-2">
              {chefs.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl overflow-hidden border border-brand-gold/25 shadow-sm flex flex-col md:flex-row" data-testid={`chef-card-${c.id}`}>
                  <div className="md:w-1/3 aspect-[4/5] md:aspect-auto flex-shrink-0 overflow-hidden">
                    {c.photo_url ? (
                      <img src={resolveMediaUrl(c.photo_url)} alt={c.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-canvas-stone flex items-center justify-center text-brand-navy/30 font-serif text-4xl">{c.name.charAt(0)}</div>
                    )}
                  </div>
                  <div className="p-6 md:p-8 flex-1">
                    <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold">{c.experience_years}+ {tt({tr:"yıl deneyim",ar:"سنة خبرة",en:"years experience",ru:"лет опыта"})}</div>
                    <h3 className="mt-2 font-serif text-2xl text-brand-navy">{c.name}</h3>
                    <div className="text-sm text-brand-navy/70">{tt(c.role)}</div>
                    <p className="mt-3 text-sm text-slate-700 leading-relaxed">{tt(c.bio)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Hotels */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <ScrollReveal>
        <SectionHeader kicker={<><MapPin size={12} className="inline mb-0.5"/> {t("nav.hotels")}</>} title={t("sections.hotels_title")} desc={t("sections.hotels_desc")} link="/hotels" />
        </ScrollReveal>
        <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4" data-testid="home-hotels-grid">
          {hotels.map((h, i) => (
            <ScrollReveal key={h.id} delay={i * 100}>
            <Link to={`/hotels/${h.id}`} className="premium-card media-zoom group block bg-white rounded-2xl overflow-hidden border border-brand-gold/20 shadow-sm" data-testid={`hotel-card-${h.id}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={resolveMediaUrl(h.images?.[0])} alt={h.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold">{h.city}</div>
                <h3 className="mt-2 font-serif text-xl text-brand-navy">{h.name}</h3>
                <div className="mt-3 text-xs text-brand-navy/70 flex items-center gap-3">
                  <span className="flex items-center gap-1"><MapPin size={12}/> {h.distance_to_haram}</span>
                  <span className="flex items-center gap-1"><Star size={12} className="text-brand-gold" fill="currentColor"/> {h.rating}</span>
                </div>
              </div>
            </Link>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="section-navy py-24" data-testid="home-testimonials">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <ScrollReveal>
            <div className="mb-12">
              <div className="text-xs uppercase tracking-[0.35em] text-brand-gold mb-3">
                <Quote size={12} className="inline mb-0.5"/> {t("sections.testimonials_title")}
              </div>
              <h2 className="font-serif text-4xl md:text-5xl">{t("sections.testimonials_title")}</h2>
            </div>
            </ScrollReveal>
            <div className="grid gap-6 md:grid-cols-3">
              {testimonials.map((tst, i) => (
                <ScrollReveal key={tst.id} delay={i * 120}>
                <div className="premium-card bg-white/8 backdrop-blur rounded-2xl p-6 border border-white/10" data-testid={`testimonial-${tst.id}`}>
                  <div className="flex gap-0.5 text-brand-gold mb-3">
                    {Array.from({ length: Math.round(tst.rating || 5) }).map((_, k) => <Star key={k} size={14} fill="currentColor"/>)}
                  </div>
                  <p className="text-canvas-cream/90 text-sm leading-relaxed italic">"{tt(tst.quote)}"</p>
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="text-brand-gold font-serif text-lg">{tst.name}</div>
                    <div className="text-xs text-canvas-cream/60">{tst.city}{tst.country ? `, ${tst.country}` : ""}</div>
                  </div>
                </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Menu preview */}
      {menu && (
        <section className="section-band py-24" data-testid="home-menu-preview">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
              <div>
                <div className="text-xs uppercase tracking-[0.35em] text-brand-orange font-semibold mb-3"><Utensils size={12} className="inline mb-0.5"/> {t("nav.menu")}</div>
                <h2 className="font-serif text-4xl md:text-5xl text-brand-navy">{tt(menu.label)}</h2>
                <p className="mt-3 text-slate-600 max-w-2xl">{t("sections.menu_desc")}</p>
              </div>
              <Link to="/menu" className="text-brand-orange hover:text-brand-orange-dark text-sm font-medium">
                {t("hero.cta_menu")} →
              </Link>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {menu.meals.slice(0, 3).map((m, i) => (
                <ScrollReveal key={m.day} delay={i * 100}>
                <div className="premium-card bg-white rounded-2xl border border-brand-gold/20 p-6 shadow-sm">
                  <div className="text-brand-orange font-serif text-xl">{m.day}</div>
                  <ul className="mt-3 space-y-2 text-sm text-slate-800">
                    <li><span className="text-brand-orange">◆ {t("sections.breakfast")}:</span> {tt(m.breakfast)}</li>
                    <li><span className="text-brand-orange">◆ {t("sections.lunch")}:</span> {tt(m.lunch)}</li>
                    <li><span className="text-brand-orange">◆ {t("sections.dinner")}:</span> {tt(m.dinner)}</li>
                  </ul>
                </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog preview */}
      {posts.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <ScrollReveal>
          <SectionHeader kicker={t("nav.blog")} title={t("sections.blog_title")} desc={t("sections.blog_desc")} link="/blog" />
          </ScrollReveal>
          <div className="mt-12 grid gap-8 md:grid-cols-3" data-testid="home-blog-preview">
            {posts.map((p, i) => (
              <ScrollReveal key={p.id} delay={i * 120}>
              <Link to={`/blog/${p.slug}`} className="premium-card media-zoom group block bg-white rounded-2xl overflow-hidden border border-brand-gold/20 shadow-sm">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={resolveMediaUrl(p.cover_image)} alt={tt(p.title)} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl text-brand-navy leading-tight line-clamp-2">{tt(p.title)}</h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-3">{tt(p.excerpt)}</p>
                </div>
              </Link>
              </ScrollReveal>
            ))}
          </div>
        </section>
      )}

      {/* Certificates strip */}
      {certs.length > 0 && (
        <section className="section-warm py-16" data-testid="home-certificates">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-xs uppercase tracking-[0.35em] text-brand-orange font-semibold mb-6 text-center">{t("sections.certificates_title")}</div>
            <div className="flex flex-wrap items-center justify-center gap-8">
              {certs.map((c) => (
                <div key={c.id} className="bg-white p-4 rounded-xl border border-brand-gold/30 flex flex-col items-center max-w-[160px]">
                  {c.image_url && <img src={resolveMediaUrl(c.image_url)} alt={tt(c.title)} className="h-16 object-contain" />}
                  <div className="mt-2 text-xs text-brand-navy text-center font-medium">{tt(c.title)}</div>
                  {c.issuer && <div className="text-[10px] text-slate-500">{c.issuer}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Instagram embedded feed (admin-curated posts) */}
      <ScrollReveal>
        <InstagramFeed />
      </ScrollReveal>

      {/* Instagram CTA */}
      {instagramUrl && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-16" data-testid="home-instagram-cta">
          <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="group relative block overflow-hidden rounded-3xl bg-gradient-to-br from-brand-orange via-brand-orange-dark to-brand-navy p-10 md:p-14 text-canvas-cream hover:shadow-2xl transition">
            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <div className="text-xs uppercase tracking-[0.35em] text-brand-gold mb-2 flex items-center gap-2"><Instagram size={14}/> Instagram</div>
                <h3 className="font-serif text-3xl md:text-4xl leading-tight">
                  {tt({
                    tr: "Ariaf'ı Instagram'da takip edin",
                    ar: "تابعنا على إنستقرام",
                    en: "Follow Ariaf on Instagram",
                    ru: "Подпишитесь на Ariaf в Instagram",
                  })}
                </h3>
                <p className="mt-2 text-canvas-cream/85">
                  {tt({
                    tr: "Mutfağımızdan günlük kareler, kısa videolar ve sofra hikayeleri.",
                    ar: "لقطات يومية، فيديوهات قصيرة وقصص من مطبخنا.",
                    en: "Daily shots from our kitchen, short videos and table stories.",
                    ru: "Ежедневные кадры с кухни, короткие видео и истории застолья.",
                  })}
                </p>
              </div>
              <div className="text-sm font-medium bg-white text-brand-navy rounded-full px-6 py-3 group-hover:scale-105 transition">@ariafcatering →</div>
            </div>
          </a>
        </section>
      )}
    </div>
  );
}

function SectionHeader({ kicker, title, desc, link }) {
  const { t } = useLang();
  return (
    <div className="flex items-end justify-between flex-wrap gap-6">
      <div>
        <div className="text-xs uppercase tracking-[0.35em] text-brand-orange font-semibold mb-3">{kicker}</div>
        <h2 className="font-serif text-4xl md:text-5xl text-brand-navy max-w-2xl">{title}</h2>
        <p className="mt-3 text-slate-600 max-w-2xl">{desc}</p>
      </div>
      {link && (
        <Link to={link} className="text-brand-orange hover:text-brand-orange-dark text-sm font-medium">
          {t("cta.all")} →
        </Link>
      )}
    </div>
  );
}

function Stat({ n, label }) {
  const { tt } = useLang();
  return (
    <div>
      <AnimatedCounter value={n} className="font-serif text-3xl md:text-5xl text-brand-gold" />
      <div className="text-xs md:text-sm uppercase tracking-widest text-canvas-cream/70 mt-1">{tt(label)}</div>
    </div>
  );
}

function WhyCard({ icon, title, desc }) {
  const { tt } = useLang();
  return (
    <div className="bg-white/70 backdrop-blur rounded-2xl p-6 border border-brand-gold/30 shadow-sm hover:shadow-xl transition">
      <div className="w-11 h-11 rounded-full bg-brand-orange/10 flex items-center justify-center mb-4">
        {icon}
      </div>
      <div className="font-serif text-xl text-brand-navy">{tt(title)}</div>
      <p className="mt-2 text-sm text-slate-700 leading-relaxed">{tt(desc)}</p>
    </div>
  );
}
